<template>
  <div class="questionnaire-container" :style="containerStyle">
    <!-- Question Cards Stack or Pause State -->
    <div class="questionnaire__content" v-if="!isPaused">
      <transition-group
        name="card"
        tag="div"
        class="questionnaire__cards-stack"
      >
        <!-- Use stable keys (question.tid) so Vue can animate correctly -->
        <div
          v-for="(question, index) in visibleQuestions"
          :key="question.tid"
          class="questionnaire__card"
          :class="{
            'questionnaire__card--active': index === 0,
            'questionnaire__card--background': index > 0,
            'questionnaire__card--answering': isAnswering && index === 0 && !isWaiting,
            'questionnaire__card--waiting': isWaiting && index === 0,
            'questionnaire__card--answered-yes': answers[question.tid] === true,
            'questionnaire__card--answered-no': answers[question.tid] === false
          }"
          :style="getStackedCardStyle(index)"
        >
          <div class="questionnaire__card-inner">
            <!-- Progress Bar (in each card) -->
            <div class="questionnaire__progress">
              <div class="questionnaire__progress-lines">
                <span
                  v-for="slashIndex in totalQuestions"
                  :key="slashIndex"
                  class="questionnaire__progress-slash"
                  :class="{ 'questionnaire__progress-slash--active': slashIndex <= currentQuestion }"
                >////</span>
              </div>
            </div>

            <h3 class="questionnaire__question-title" v-html="question.title"></h3>

            <!-- Action Buttons (only on active card) -->
            <div class="questionnaire__actions" v-if="index === 0">
              <button
                v-if="answers[question.tid] !== false"
                type="button"
                class="questionnaire__btn questionnaire__btn--yes button"
                @click="answerQuestion(true)"
                :disabled="isAnswering || isWaiting"
              >
                <span class="button__text-wrapper">
                  <span class="button__text">{{ t.yes }}</span>
                  <span class="button__text">{{ t.yes }}</span>
                </span>
                <span class="button__overlay"></span>
              </button>

              <button
                v-if="answers[question.tid] !== true"
                type="button"
                class="questionnaire__btn questionnaire__btn--no button"
                @click="answerQuestion(false)"
                :disabled="isAnswering || isWaiting"
              >
                <span class="button__text-wrapper">
                  <span class="button__text">{{ t.no }}</span>
                  <span class="button__text">{{ t.no }}</span>
                </span>
                <span class="button__overlay"></span>
              </button>
            </div>
          </div>
        </div>
      </transition-group>

      <!-- Loading State -->
      <div v-if="isLoading" class="questionnaire__loading">
        <div class="questionnaire__spinner"></div>
        <p>{{ t.loadingQuestions }}</p>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="questionnaire__error">
        <p>{{ error }}</p>
        <Button
          :text="t.retryAgain"
          variant="primary"
          @click="initializeQuestionnaire"
        />
      </div>
    </div>

    <!-- Pause State -->
    <div class="questionnaire__pause" v-else>
      <div class="questionnaire__pause-content">
        <!-- Progress Bar (showing completion) -->
        <div class="questionnaire__progress">
          <div class="questionnaire__progress-lines">
            <span
              v-for="slashIndex in totalQuestions"
              :key="slashIndex"
              class="questionnaire__progress-slash questionnaire__progress-slash--active"
            >////</span>
          </div>
        </div>

        <h3 class="questionnaire__pause-title">{{ t.allQuestionsAnswered }}</h3>
        <p class="questionnaire__pause-description">
          {{ t.questionsImpression }}
        </p>
        <div class="questionnaire__pause-actions">
          <Button
            :text="t.reopenForm"
            variant="primary"
            @click="reopenModal"
          />
          <Button
            :text="t.reloadQuestions"
            variant="reset"
            @click="resetQuestionnaire"
          />
        </div>
      </div>
    </div>

    <!-- Results Modal (teleported to body with transitions) -->
    <teleport to="#app">
      <transition name="overlay-fade">
        <div
          v-if="showModal"
          class="questionnaire__modal-overlay"
          @click="handleModalOverlayClick"
          role="dialog"
          aria-modal="true"
        >
          <transition name="modal-scale">
            <div class="questionnaire__modal" @click.stop>
              <div class="questionnaire__modal-header">
                <button
                  type="button"
                  class="questionnaire__modal-close"
                  @mouseenter="onModalCloseHoverEnter"
                  @mouseleave="onModalCloseHoverLeave"
                  :style="{ transform: `rotate(${rotation}deg)` }"
                  @click="closeModal"
                  aria-label="Close"
                >
                <i class="icon-x" aria-hidden="true"></i>
                </button>
              </div>

              <div class="questionnaire__modal-body">
                <h3 class="questionnaire__modal-title">{{ getModalTitle() }}</h3>

                <!-- Results Summary -->
                <div class="questionnaire__results" v-if="!formSubmitted">
                  <div class="questionnaire__results-list">
                    <div
                      v-for="questionId in answeredQuestions"
                      :key="questionId"
                      class="questionnaire__result-item"
                      @click="toggleAnswer(questionId)"
                    >
                      <span
                        class="questionnaire__result-question"
                        v-html="getQuestionById(questionId).title"
                        :class="{
                          'questionnaire__result-question--yes': answers[questionId],
                          'questionnaire__result-question--no': !answers[questionId]
                        }"
                      ></span>
                      <span
                        class="questionnaire__result-answer"
                        :class="{
                          'questionnaire__result-answer--yes': answers[questionId],
                          'questionnaire__result-answer--no': !answers[questionId]
                        }"
                      >
                        {{ answers[questionId] ? t.yes : t.no }}
                      </span>
                    </div>
                  </div>
                </div>

                <!-- Contact Form Section -->
                <div class="questionnaire__contact-section" v-if="!formSubmitted">
                  <h4 class="questionnaire__contact-title">{{ t.personalInformation }}</h4>
                  <QuestionnaireWebform
                    ref="webform"
                    :answers="answers"
                    webform-id="questionnaire_results"
                    :saved-form-data="savedFormData"
                    @submitted="handleFormSubmitted"
                    @form-validity-changed="handleFormValidityChanged"
                    @form-data-changed="handleFormDataChanged"
                  />
                </div>

                <!-- Success Message (Removed, now shows globally) -->
              </div>
              <!-- Modal Footer with Submit Button -->
              <div class="questionnaire__modal-footer" v-if="!formSubmitted">
                <div class="questionnaire__modal-footer-gradient"></div>
                <Button
                  :text="isSubmitting ? t.submitting : t.sendMessage"
                  variant="primary"
                  custom-class="questionnaire__modal-submit"
                  :disabled="!isFormValid || isSubmitting"
                  :loading="isSubmitting"
                  @click="triggerFormSubmit"
                />
              </div>
            </div>
          </transition>
        </div>
      </transition>
    </teleport>
  </div>
</template>

<script>
import translationStrings from '../utils/translationStrings';
import QuestionnaireWebform from './QuestionnaireWebform.vue';
import Button from './Button.vue';

export default {
  name: 'Questionnaire',
  components: { QuestionnaireWebform, Button },
  data() {
    return {
      t: translationStrings,
      rotation: 0,
      isHovering: false,
      allQuestions: [],
      availableQuestions: [], // Questions that haven't been used yet
      usedQuestions: [], // Questions that have been used
      shuffledQuestions: [],
      currentQuestion: 1,
      totalQuestions: 5,
      answers: {},
      answeredQuestions: [], // Track the order questions were answered
      showModal: false,
      isPaused: false,
      isLoading: true,
      isAnswering: false,
      isWaiting: false, // New wait state
      error: null,
      formSubmitted: false,
      webformElement: null,
      originalWebformContainer: null,
      pageType: 'optimization',
      previouslyFocusedEl: null,
      isFormValid: false,
      isSubmitting: false,
      containerHeight: null,
      savedFormData: null,
    };
  },

  computed: {
    visibleQuestions() {
      if (this.currentQuestion > this.totalQuestions) return [];
      const startIndex = this.currentQuestion - 1;
      // Show max 3 cards total: 1 active + 2 background
      const maxCards = 3;
      return this.shuffledQuestions.slice(startIndex, startIndex + maxCards);
    },
    containerStyle() {
      return this.containerHeight
        ? { height: `${this.containerHeight}px` }
        : {};
    },
  },

  mounted() {
    this.initializeQuestionnaire();
    this.detectPageType();
    this.updateContainerHeight();
    document.addEventListener('keydown', this.onKeydown);
    window.addEventListener('resize', this.updateContainerHeight);
  },

  beforeUnmount() {
    document.removeEventListener('keydown', this.onKeydown);
    window.removeEventListener('resize', this.updateContainerHeight);
    this.unlockScroll();
  },

  watch: {
    visibleQuestions() {
      this.updateContainerHeight();
    },
    isPaused() {
      this.updateContainerHeight();
    }
  },

  methods: {
    initializeQuestionnaire() {
      this.loadQuestions();
    },

    detectPageType() {
      const container = document.querySelector('#questionnaire');
      if (container?.dataset.pageType) {
        this.pageType = container.dataset.pageType;
      }
    },

    updateContainerHeight() {
      this.$nextTick(() => {
        const cards = this.$el.querySelectorAll('.questionnaire__card');
        const pauseContent = this.$el.querySelector('.questionnaire__pause-content');

        let maxHeight = 0;

        if (pauseContent) {
           maxHeight = pauseContent.offsetHeight;
        }

        if (cards.length) {
          cards.forEach(card => {
            maxHeight = Math.max(maxHeight, card.offsetHeight);
          });
        }

        this.containerHeight = maxHeight > 0 ? maxHeight : null;
      });
    },

    getModalTitle() {
      return this.pageType === 'marketing'
        ? this.t.requestSupportMarketing
        : this.t.requestSupportOptimization;
    },

    onModalCloseHoverEnter() {
      if (!this.isHovering) {
        this.isHovering = true;
        this.rotation += 90;
      }
    },

    onModalCloseHoverLeave() {
      this.isHovering = false;
    },

    loadQuestions() {
      this.isLoading = true;
      this.error = null;

      try {
        const container = document.querySelector('#questionnaire');
        const questionsData = container?.dataset.questions;

        if (questionsData) {
          this.allQuestions = JSON.parse(questionsData);

          if (this.allQuestions.length === 0) {
            this.error = this.t.noQuestionsAvailable;
            return;
          }

          // Initialize available questions if not already set
          if (this.availableQuestions.length === 0) {
            this.availableQuestions = [...this.allQuestions];
          }

          this.selectNewQuestions();
        } else {
          this.error = this.t.questionsNotFound;
        }
      } catch (err) {
        this.error = this.t.errorLoadingQuestions;
        console.error('Error loading questions:', err);
      } finally {
        this.isLoading = false;
      }
    },

    selectNewQuestions() {
      const targetQuestions = this.totalQuestions; // Always try to get 5 questions
      let selectedQuestions = [];

      // First, get as many unused questions as possible
      const shuffledAvailable = this.shuffleArray([...this.availableQuestions]);
      const availableCount = Math.min(targetQuestions, shuffledAvailable.length);

      if (availableCount > 0) {
        const newQuestions = shuffledAvailable.slice(0, availableCount);
        selectedQuestions = [...newQuestions];

        // Move these questions from available to used
        newQuestions.forEach(selectedQuestion => {
          const index = this.availableQuestions.findIndex(q => q.tid === selectedQuestion.tid);
          if (index > -1) {
            this.availableQuestions.splice(index, 1);
            this.usedQuestions.push(selectedQuestion);
          }
        });

      }

      // If we need more questions to reach 5, fill from used questions
      const stillNeeded = targetQuestions - selectedQuestions.length;
      if (stillNeeded > 0 && this.usedQuestions.length > 0) {
        const shuffledUsed = this.shuffleArray([...this.usedQuestions]);
        const additionalQuestions = shuffledUsed.slice(0, stillNeeded);
        selectedQuestions = [...selectedQuestions, ...additionalQuestions];
      }

      // If we still don't have enough (edge case with very few total questions)
      if (selectedQuestions.length < targetQuestions && this.allQuestions.length > 0) {
        const shuffledAll = this.shuffleArray([...this.allQuestions]);
        const questionsNeeded = targetQuestions - selectedQuestions.length;

        // Add more questions from all questions, avoiding duplicates
        const existingIds = selectedQuestions.map(q => q.tid);
        const additionalFromAll = shuffledAll
          .filter(q => !existingIds.includes(q.tid))
          .slice(0, questionsNeeded);

        selectedQuestions = [...selectedQuestions, ...additionalFromAll];
      }

      this.shuffledQuestions = selectedQuestions;
    },

    shuffleArray(array) {
      const shuffled = [...array];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      return shuffled;
    },

    answerQuestion(answer) {
      if (this.isAnswering || this.isWaiting || this.currentQuestion > this.totalQuestions) return;

      this.isAnswering = true;

      const currentQuestionData = this.shuffledQuestions[this.currentQuestion - 1];

      // Store the answer immediately to apply answered-yes/no classes
      this.answers[currentQuestionData.tid] = answer;

      // Track the order this question was answered
      this.answeredQuestions.push(currentQuestionData.tid);

      // Sequence: answering -> wait -> leave
      setTimeout(() => {
        this.isAnswering = false;
        this.isWaiting = true; // Enter wait state
        setTimeout(() => {
          this.isWaiting = false;
          this.currentQuestion++; // Trigger leave animation
          setTimeout(() => {
            if (this.currentQuestion > this.totalQuestions) {
              this.showResults();
            }
          }, 800); // Matches leave transition duration
        }, 200); // Wait state duration
      }, 300); // Answering animation duration
    },

    getStackedCardStyle(index) {
      const baseZIndex = 10;
      const widthReduction = index * 40; // Gradual width reduction

      return {
        width: `calc(100% - ${widthReduction}px)`,
        top: `${widthReduction / 4}px`,
        marginLeft: `${widthReduction / 2}px`, // Center the narrower cards
        zIndex: baseZIndex - index,
      };
    },

    showResults() {
      this.previouslyFocusedEl = document.activeElement;
      this.lockScroll();
      this.showModal = true;
    },

    getQuestionById(questionId) {
      // First try to find in current shuffled questions
      let question = this.shuffledQuestions.find(q => q.tid == questionId);

      // If not found, search in all questions (for edge cases)
      if (!question) {
        question = this.allQuestions.find(q => q.tid == questionId);
      }

      return question || { title: this.t.unknownQuestion };
    },

    toggleAnswer(questionId) {
      this.answers[questionId] = !this.answers[questionId];
    },

    handleModalOverlayClick() {
      this.closeModal();
    },

    closeModal() {
      this.showModal = false;
      this.isPaused = true;
      this.unlockScroll();
      this.restoreFocus();
    },

    showGlobalStatusMessage(message) {
      // Find or create global Drupal messages container
      let container = document.querySelector('[data-drupal-messages]');
      if (!container) {
        // Fallback to data-drupal-messages-fallback if main container isn't rendered
        container = document.querySelector('[data-drupal-messages-fallback]');
        if (container) {
            container.setAttribute('data-drupal-messages', '');
        } else {
            // Last resort: insert before header or prepend to app/body
            const app = document.querySelector('#app') || document.body;
            const header = document.querySelector('header');
            container = document.createElement('div');
            container.setAttribute('data-drupal-messages', '');
            if (header && header.parentNode) {
              header.before(container);
            } else {
              app.prepend(container);
            }
        }
      }

      container.style.display = '';

      // Create message element
      const messageHtml = `
        <div role="contentinfo" aria-label="Status message" class="status-message status-message--status" data-message-type="status">
          <div class="status-message__icon">
            <i class="icon-circle-check" aria-hidden="true"></i>
          </div>
          <div class="status-message__content">
            <div class="status-message__text">${message}</div>
          </div>
          <button type="button" class="status-message__close animate-rotate" aria-label="Close message" data-dismiss="status-message">
            <i class="icon-x" aria-hidden="true"></i>
          </button>
        </div>
      `;

      // Prepend to show at the top
      container.insertAdjacentHTML('afterbegin', messageHtml);

      // Re-attach Drupal behaviors to initialize the close button and auto-dismiss
      if (Drupal && Drupal.attachBehaviors) {
        Drupal.attachBehaviors(container);
      }
    },

    reopenModal() {
      this.previouslyFocusedEl = document.activeElement;
      this.lockScroll();
      this.showModal = true;
    },

    resetQuestionnaire() {
      this.currentQuestion = 1;
      this.answers = {};
      this.answeredQuestions = []; // Reset the order tracking
      this.showModal = false;
      this.isPaused = false;
      this.formSubmitted = false;
      this.isAnswering = false;
      this.isWaiting = false;
      this.error = null;
      this.isFormValid = false;
      this.isSubmitting = false;
      this.savedFormData = null;
      this.unlockScroll();
      this.restoreFocus();

      // Select new questions (will auto-reset if no available questions)
      this.selectNewQuestions();
    },

    handleFormValidityChanged(isValid, isSubmitting) {
      this.isFormValid = isValid;
      this.isSubmitting = isSubmitting;
    },

    handleFormSubmitted() {
      this.formSubmitted = true;
      this.savedFormData = null;

      // Close modal immediately and show global message
      this.closeModal();
      this.showGlobalStatusMessage(this.t.messageSubmitted);

      // Reset questionnaire state
      this.resetQuestionnaire();
    },

    handleFormDataChanged(formData) {
      this.savedFormData = formData;
    },

    triggerFormSubmit() {
      this.$refs.webform?.submitForm();
    },

    onKeydown(e) {
      if (!this.showModal) return;
      if (e.key === 'Escape') this.closeModal();
    },

    lockScroll() {
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
    },

    unlockScroll() {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    },

    restoreFocus() {
      if (this.previouslyFocusedEl && this.previouslyFocusedEl.focus) {
        this.previouslyFocusedEl.focus();
      }
    }
  },
};
</script>
